/**
 * Shared plumbing for the per-area API clients (Phase 5a split of lib/api.js).
 *
 * Everything here is moved verbatim from the former single-file lib/api.js:
 * the token keys and their per-surface order, the OAuth session fallback,
 * the axios factory with its auth-header request interceptor and the
 * `tappd:unauthorized` 401 response interceptor, and errorMessage().
 *
 * Token order is load-bearing (see the plan's venue-partner vs
 * business-owner shadowing note) — do not reorder without an ADR.
 *
 * Area modules (patron, business, venue, admin, billing) each create their
 * instance with createClient(surface) and add thin endpoint functions.
 * Endpoint functions return the axios promise (the full response), so a page
 * can swap `businessApi.get('/business/deals')` for `getBusinessDeals()` 1:1.
 */
import axios from 'axios';
import { BACKEND_URL } from '../../config';

export const TOKEN_KEYS = {
  patron: ['token'],
  business: ['businessOwnerToken', 'token'],
  venue: ['token'],
  admin: ['adminToken', 'token'],
};

const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};

export const getToken = (surface = 'patron') => {
  for (const key of TOKEN_KEYS[surface] || TOKEN_KEYS.patron) {
    const v = safeGet(key);
    if (v) return v;
  }
  return null;
};

export const getOAuthSessionToken = () => {
  const stored = safeGet('oauth_session_token');
  if (stored) return stored;
  if (typeof document === 'undefined') return null;
  const cookie = document.cookie.split('; ').find((row) => row.startsWith('session_token='));
  return cookie ? cookie.split('=')[1] : null;
};

/** Headers for the odd call that cannot go through the client (e.g. fetch for file uploads). */
export const authHeaders = (surface = 'patron') => {
  const token = getToken(surface);
  if (token) return { Authorization: `Bearer ${token}` };
  const oauth = getOAuthSessionToken();
  return oauth ? { 'X-OAuth-Session-Token': oauth } : {};
};

export const UNAUTHORIZED_EVENT = 'tappd:unauthorized';

export const createClient = (surface) => {
  const client = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: surface === 'admin' });
  client.interceptors.request.use((config) => {
    const headers = config.headers || {};
    const hasAuth = headers.Authorization || headers.authorization || headers['X-OAuth-Session-Token'];
    if (!hasAuth) Object.assign(headers, authHeaders(surface));
    config.headers = headers;
    return config;
  });
  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error?.response?.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { surface, url: error.config?.url } }));
      }
      return Promise.reject(error);
    },
  );
  client.surface = surface;
  return client;
};

/** Human-readable message from an axios error (FastAPI `detail` first). */
export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
};

/**
 * `?a=1&b=2` from an object (undefined/null/'' entries dropped), '' when empty.
 * Used where today's pages put query flags in the path instead of `params`.
 */
export const qs = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};
